import React, { useState } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import toast from 'react-hot-toast';

const CURRICULUM_CHAPTERS = [
    {
        title: "🚀 Opening Traps & Principles",
        lessons: [
            {
                title: "Scholar's Mate Defense",
                fen: "r1bqkbnr/pppp1ppp/2n5/4p3/2B1P3/5Q2/PPPP1PPP/RNB1K1NR b KQkq - 3 3",
                desc: "Teach students how to defend against early Queen attacks targeting the f7 pawn. Discuss developing the Knight to c6 and blocking with g6 / Nf6.",
                difficulty: "Beginner"
            },
            {
                title: "Fried Liver Attack",
                fen: "r1bqkb1r/pppp1ppp/2n2n2/4p1N1/2B1P3/8/PPPP1PPP/RNBQK2R b KQkq - 5 4",
                desc: "Demonstrate the dangerous attack on f7. Discuss the move d5 to block the Bishop, and the counterplay options.",
                difficulty: "Intermediate"
            },
            {
                title: "Italian Game (Main Line)",
                fen: "r1bqkbnr/pppp1ppp/2n5/4p3/2B1P3/5N2/PPPP1PPP/RNBQK2R b KQkq - 3 3",
                desc: "Explore classical development: control the center with e4, develop Knights and Bishops towards the center, and prepare kingside castling.",
                difficulty: "Beginner"
            }
        ]
    },
    {
        title: "🧩 Tactical Patterns",
        lessons: [
            {
                title: "The Power of the Fork",
                fen: "r3k2r/ppp2ppp/2np1n2/2b1p3/2B1P1b1/2NP1N2/PPPN1PPP/R3K2R w KQkq - 0 1",
                desc: "Find coordinates to launch a Knight fork attacks. Teach how to spot forks on c7 or f3.",
                difficulty: "Beginner"
            },
            {
                title: "Pinning & Exploiting Pins",
                fen: "r1bqk2r/pppp1ppp/2n2n2/2b1p3/2B1P3/3P1N2/PPP2PPP/RNBQK2R w KQkq - 1 5",
                desc: "Show how to tie down enemy pieces to their King or Queen. Pin the f6 knight with Bg5, then attack the pinned piece.",
                difficulty: "Intermediate"
            },
            {
                title: "Back-Rank Mate Threat",
                fen: "6k1/5ppp/8/8/8/8/8/3R2K1 w - - 0 1",
                desc: "Demonstrate mates where the King is trapped behind its own pawns. Move Rook to d8 for checkmate.",
                difficulty: "Beginner"
            }
        ]
    },
    {
        title: "🏰 Endgame Mastery",
        lessons: [
            {
                title: "King & Pawn opposition",
                fen: "8/8/8/5k2/5P2/5K2/8/8 w - - 0 1",
                desc: "Teach opposition rules. Who controls the direct squares wins the promotion race.",
                difficulty: "Advanced"
            },
            {
                title: "King & Rook Checkmate",
                fen: "k7/8/8/8/8/8/8/1R3K2 w - - 0 1",
                desc: "Instruct students on boxing in the enemy king to the edge using Rook ranks, supported by the King.",
                difficulty: "Intermediate"
            }
        ]
    }
];

export default function CoachCurriculum({ activeClass }) {
    const [expandedChapter, setExpandedChapter] = useState(0);

    const handleLoadBoard = async (fen, title) => {
        if (!activeClass?.id) {
            toast.error("Please launch or join a live classroom first from Home HQ to load lesson boards!");
            return;
        }

        const loading = toast.loading(`Loading "${title}" onto Whiteboard...`);
        try {
            await updateDoc(doc(db, "classes", activeClass.id), {
                currentFen: fen,
                boardHighlights: {},
                boardArrows: [],
                boardItems: {},
                studentFens: {} // Reset any active solo boards to match
            });
            toast.success(`"${title}" is now loaded onto the whiteboard! 🎓♟️`, { id: loading });
        } catch (err) {
            console.error(err);
            toast.error("Failed to load lesson position.", { id: loading });
        }
    };

    return (
        <div style={{ background: 'var(--bg-primary)', padding: '30px', borderRadius: '20px', boxShadow: 'var(--panel-shadow)', border: '1px solid rgba(255,255,255,0.55)', width: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                <span style={{ fontSize: '32px' }}>📚</span>
                <div>
                    <h2 style={{ color: 'var(--text-primary)', margin: 0, fontSize: '20px' }}>Academy Lesson Library</h2>
                    <p style={{ color: 'var(--text-secondary)', margin: '4px 0 0 0', fontSize: '13px', fontWeight: '500' }}>Select and load pre-configured curriculum lessons onto the whiteboard instantly.</p>
                </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', marginTop: '25px' }}>
                {CURRICULUM_CHAPTERS.map((chapter, chIdx) => {
                    const isExpanded = expandedChapter === chIdx;
                    return (
                        <div key={chIdx} style={{ 
                            background: '#e0e5ec', 
                            borderRadius: '16px', 
                            border: '1px solid rgba(255,255,255,0.55)', 
                            boxShadow: isExpanded ? 'var(--panel-shadow-hover)' : 'var(--panel-shadow)',
                            overflow: 'hidden', 
                            transition: 'all 0.25s ease-in-out' 
                        }}>
                            {/* Chapter Header */}
                            <div 
                                onClick={() => setExpandedChapter(isExpanded ? null : chIdx)}
                                style={{ 
                                    padding: '18px 24px', 
                                    display: 'flex', 
                                    justifyContent: 'space-between', 
                                    alignItems: 'center', 
                                    cursor: 'pointer',
                                    background: isExpanded ? 'var(--btn-hover)' : 'transparent',
                                    borderLeft: '5px solid #f59e0b',
                                    userSelect: 'none'
                                }}
                            >
                                <span style={{ fontSize: '16px', color: 'var(--text-primary)', fontWeight: '800' }}>{chapter.title}</span>
                                <span style={{ color: 'var(--text-secondary)', fontSize: '13px', transform: isExpanded ? 'rotate(180deg)' : 'none', transition: '0.3s' }}>▼</span>
                            </div>

                            {/* Chapter Lessons List */}
                            {isExpanded && (
                                <div style={{ 
                                    padding: '20px 24px', 
                                    background: '#e0e5ec', 
                                    borderTop: '1px solid rgba(255,255,255,0.45)', 
                                    boxShadow: 'inset 3px 3px 8px rgba(163,177,198,0.2), inset -3px -3px 8px rgba(255,255,255,0.75)',
                                    display: 'flex', 
                                    flexDirection: 'column', 
                                    gap: '20px' 
                                }}>
                                    {chapter.lessons.map((lesson, lIdx) => (
                                        <div key={lIdx} style={{ 
                                            background: '#e0e5ec', 
                                            borderRadius: '12px', 
                                            padding: '16px 20px', 
                                            border: '1px solid rgba(255, 255, 255, 0.55)', 
                                            boxShadow: '4px 4px 10px rgba(163,177,198,0.4), -4px -4px 10px rgba(255,255,255,0.85)',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '12px'
                                        }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                                                <h4 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '15px', fontWeight: 'bold' }}>{lesson.title}</h4>
                                                <span style={{ 
                                                    fontSize: '11px', 
                                                    fontWeight: 'bold', 
                                                    padding: '3px 8px', 
                                                    borderRadius: '10px', 
                                                    background: lesson.difficulty === 'Beginner' ? 'rgba(16, 185, 129, 0.12)' : lesson.difficulty === 'Intermediate' ? 'rgba(245, 158, 11, 0.12)' : 'rgba(239, 68, 68, 0.12)', 
                                                    color: lesson.difficulty === 'Beginner' ? '#10b981' : lesson.difficulty === 'Intermediate' ? '#f59e0b' : '#ef4444',
                                                    border: lesson.difficulty === 'Beginner' ? '1px solid rgba(16, 185, 129, 0.2)' : lesson.difficulty === 'Intermediate' ? '1px solid rgba(245, 158, 11, 0.2)' : '1px solid rgba(239, 68, 68, 0.2)'
                                                }}>
                                                    {lesson.difficulty}
                                                </span>
                                            </div>

                                            <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '13px', lineHeight: '1.6', fontWeight: '500' }}>
                                                {lesson.desc}
                                            </p>

                                            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '5px' }}>
                                                <button 
                                                    onClick={() => handleLoadBoard(lesson.fen, lesson.title)}
                                                    style={{ padding: '8px 16px', background: 'var(--bg-primary)', border: '1px solid rgba(255,255,255,0.6)', color: '#3b82f6', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', fontSize: '12px', boxShadow: '2px 2px 5px rgba(163,177,198,0.25), -2px -2px 5px rgba(255,255,255,0.7)', transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: '6px' }}
                                                >
                                                    🎓 Load to Whiteboard
                                                </button>
                                                <button 
                                                    onClick={() => { navigator.clipboard.writeText(lesson.fen); toast.success("Lesson FEN Copied!"); }}
                                                    style={{ padding: '8px 16px', background: 'var(--bg-primary)', border: '1px solid rgba(255,255,255,0.6)', color: 'var(--text-primary)', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', fontSize: '12px', boxShadow: '2px 2px 5px rgba(163,177,198,0.25), -2px -2px 5px rgba(255,255,255,0.7)', transition: 'all 0.2s' }}
                                                >
                                                    📋 Copy FEN
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
